import {
  TellescopeModelFromName,
  useAuth,
  useCachedTags,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { Badge, Textarea } from "@tellescope/ui";
import { Button, Item, ItemContent, ItemTitle, ItemDescription, ItemActions, Spinner } from "@tellescope/design-system";
import { formatDateTime, sanitizeEmailHTMLContent } from "@tellescope/utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditableDateCell, EditablePersonCell, PriorityCell, StageCell, TagsCell } from "./cells";
import { PersonCell } from "./cells/PersonCell";
import { Bell, Calendar, Flag, GitBranch, Tag, Target, User, Workflow } from "lucide-react";
import { TicketContext } from "./TicketContext";
import { TicketActions } from "./TicketActions";
import { TicketAttachment } from "./TicketAttachment";
import { Link } from "react-router-dom";
import { ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME } from "@tellescope/constants";
import { useSidebarShallowStore } from "../../store/sidebar-store";

const enduserFieldDisplayNames: Record<string, string> = Object.fromEntries(
  Object.entries(ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME)
);

export interface TicketDetailsProps {
  ticketId: string;
  ticket?: TellescopeModelFromName<"tickets">;
}

export const TicketDetails = ({ ticketId, ticket: providedTicket }: TicketDetailsProps) => {
  const { t } = useTranslation("tickets");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedOrganization } = useAuth();
  const { openSidebar } = useSidebarShallowStore("openSidebar");

  const ticketQuery = useTellescopeQuery({
    model: "tickets",
    method: "getOne",
    request: ticketId,
    enabled: !!ticketId && !providedTicket,
  });

  const updateTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "updateOne",
  });

  const ticket = providedTicket ?? ticketQuery.data;

  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: ticket?.enduserId ?? "",
    enabled: !!ticket?.enduserId,
  });

  const queueQuery = useTellescopeQuery({
    model: "ticket_queues",
    method: "getOne",
    request: ticket?.dequeuedFrom ?? "",
    enabled: !!ticket?.dequeuedFrom,
  });

  const parentTicketQuery = useTellescopeQuery({
    model: "tickets",
    method: "getOne",
    request: ticket?.parentTicketId ?? "",
    enabled: !!ticket?.parentTicketId,
  });

  const journeyQuery = useTellescopeQuery({
    model: "journeys",
    method: "getOne",
    request: ticket?.journeyId ?? "",
    enabled: !!ticket?.journeyId,
  });

  const orgCustomFields = useMemo(
    () => selectedOrganization?.settings?.endusers?.customFields ?? [],
    [selectedOrganization?.settings?.endusers?.customFields]
  );

  const enduser = enduserQuery.data;
  const getProfileFieldValue = useCallback(
    (fieldKey: string): string => {
      if (!enduser) return "";
      const isCustomField = orgCustomFields.some((f) => f.field === fieldKey);
      if (isCustomField) {
        const val = enduser.fields?.[fieldKey];
        return val !== null && val !== undefined ? String(val) : "";
      }
      const val = enduser[fieldKey as keyof typeof enduser];
      if (val !== null && val !== undefined) return String(val);
      // Fallback: check enduser.fields for custom entity type fields not in orgCustomFields
      const customVal = enduser.fields?.[fieldKey];
      return customVal !== null && customVal !== undefined ? String(customVal) : "";
    },
    [enduser, orgCustomFields]
  );

  const profileFieldKeys = useMemo<string[]>(() => {
    if (ticket?.contextEnduserFields?.length)
      return ticket.contextEnduserFields.filter((key) => getProfileFieldValue(key));
    return queueQuery.data?.enduserFields ?? [];
  }, [ticket?.contextEnduserFields, queueQuery.data?.enduserFields, getProfileFieldValue]);
  const { data: tagOptions } = useCachedTags({ model: "tickets" });

  const relationshipIds = useMemo(() => {
    return enduser?.relationships?.map((rel) => rel.id) ?? [];
  }, [enduser?.relationships]);

  const relationshipEndusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: relationshipIds },
    enabled: relationshipIds.length > 0,
  });

  const relationshipsWithContacts = useMemo(() => {
    if (!relationshipEndusersQuery.data?.matches || !enduser?.relationships) return [];
    const contactsById = new Map(relationshipEndusersQuery.data.matches.map((c) => [c.id, c]));
    return enduser.relationships.reduce<{ contact: TellescopeModelFromName<"endusers">; type: string }[]>(
      (acc, rel) => {
        const contact = contactsById.get(rel.id);
        if (contact) acc.push({ contact, type: rel.type ?? "Unknown" });
        return acc;
      },
      []
    );
  }, [relationshipEndusersQuery.data, enduser?.relationships]);

  const relatedJourneyEndusers = useMemo(() => {
    if (!ticket?.journeyId || !ticket?.enduserId || !relationshipEndusersQuery.data?.matches) return [];
    return relationshipEndusersQuery.data.matches.filter((e) => {
      const journeys = e.journeys;
      return journeys?.[ticket.journeyId!] !== undefined;
    });
  }, [relationshipEndusersQuery.data, ticket?.journeyId, ticket?.enduserId]);

  const orgRecord = selectedOrganization;
  const canvasURL = typeof orgRecord?.canvasURL === "string" ? orgRecord.canvasURL : undefined;
  const hasCanvasReference =
    !!enduser &&
    ((enduser.source === "Canvas" && !!enduser.externalId) ||
      (enduser.references ?? []).some((ref) => ref.type === "Canvas"));

  if (!ticket) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center">
        <Spinner />
        <p>{t("Loading ticket...")}</p>
      </div>
    );
  }

  const onAddTag = (tag: string) => {
    updateTicketMutation.mutateAsync({
      id: ticketId,
      tags: [...(ticket.tags ?? []), tag],
      _options: { replaceObjectFields: true },
    });
  };

  const onRemoveTag = (tag: string) => {
    updateTicketMutation.mutateAsync({
      id: ticketId,
      tags: (ticket.tags ?? []).filter((existingTag) => existingTag !== tag),
      _options: { replaceObjectFields: true },
    });
  };

  const getProfileFieldLabel = (fieldKey: string): string => {
    const customField = orgCustomFields.find((f) => f.field === fieldKey);
    if (customField) return fieldKey;
    return enduserFieldDisplayNames[fieldKey] ?? fieldKey;
  };

  const activeSnooze = ticket.snoozes?.find((s) => new Date(String(s.until)) > new Date());

  return (
    <div className="w-full flex flex-col overflow-hidden" ref={containerRef}>
      <div className="border-b-[1px] border-b-gray-200 px-6 pb-2 pt-4 w-full flex flex-col gap-2">
        {/* Due date */}
        <div className="flex gap-2 items-center text-xs text-muted-foreground flex-wrap w-full">
          <p className="font-medium">{t("Due:")}</p>
          <EditableDateCell
            value={ticket.dueDateInMS ?? undefined}
            onChange={(newDate) => {
              updateTicketMutation.mutateAsync({
                id: ticketId,
                dueDateInMS: newDate?.getTime() ?? undefined,
              });
            }}
            relative={{ unit: "weeks", amount: 1, direction: "both" }}
            variant="text"
          />
        </div>

        {/* Opened / updated */}
        <div className="flex gap-1 items-center text-xs text-muted-foreground">
          <p className="font-medium">{t("Opened/updated:")}</p>
          <p className="font-normal">
            {formatDateTime(ticket.updatedAt, "MMM D, YYYY at hh:mm a", {
              unit: "weeks",
              amount: 1,
              direction: "both",
            })}
          </p>
        </div>

        {/* Close reason */}
        {ticket.closedAt && ticket.closedForReason && (
          <div className="flex gap-1 items-center text-xs text-muted-foreground">
            <p className="font-medium">{t("Close reason:")}</p>
            <Badge variant="secondary" className="text-xs h-5">
              {ticket.closedForReason}
            </Badge>
          </div>
        )}

        {/* Patient · Priority · Stage · Owner */}
        <div className="flex gap-3 items-center justify-start mt-4 mb-4 flex-wrap">
          {enduser && (
            <div className="flex gap-2 items-center">
              <div className="flex gap-1 items-center text-xs text-muted-foreground">
                <User size={16} />
                <div className="font-medium">{t("Patient:")}</div>
              </div>
              <PersonCell
                person={enduser}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openSidebar("ContactDetails", { enduserId: enduser.id });
                }}
              />
            </div>
          )}
          <div
            className="flex gap-3 items-center text-muted-foreground text-xs mr-4"
            data-testid="ticket-priority-field"
          >
            <div className="flex gap-2 items-center">
              <Flag className="w-3 h-3" />
              <p className="font-medium">{t("Priority: ")}</p>
            </div>
            <PriorityCell priority={ticket.priority} ticketId={ticketId} />
          </div>
          <div className="flex gap-3 items-center text-muted-foreground text-xs mr-4" data-testid="ticket-stage-field">
            <div className="flex gap-2 items-center">
              <Target className="w-3 h-3" />
              <p className="font-medium">{t("Stage: ")}</p>
            </div>
            <StageCell
              stage={ticket.closedAt ? "Completed" : ticket.stage}
              ticketId={ticketId}
              disableChangeStage={!!ticket.closedAt}
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex gap-1 items-center text-xs text-muted-foreground">
              <User size={16} />
              <div className="font-medium">{t("Owner:")}</div>
            </div>
            <EditablePersonCell
              mode="user"
              value={ticket.owner}
              onChange={(owner) => {
                updateTicketMutation.mutateAsync({
                  id: ticketId,
                  owner: owner ?? "",
                });
              }}
              getBadgeColors={() => ({
                bgColor: "bg-white",
                textColor: "text-muted-foreground",
              })}
            />
          </div>
          {/* Type */}
          {ticket.type && (
            <div className="flex gap-1 items-center text-xs text-muted-foreground">
              <p className="font-medium">{t("Type:")}</p>
              <Badge variant="outline" className="text-xs h-5">
                {ticket.type}
              </Badge>
            </div>
          )}

          {/* Tags */}
          <div className="flex gap-2 items-center text-xs text-muted-foreground flex-wrap">
            <div className="flex gap-1 items-center shrink-0">
              <Tag className="w-3 h-3" />
              <p className="font-medium">{t("Tags:")}</p>
            </div>
            <TagsCell
              tags={ticket.tags ?? []}
              availableOptions={tagOptions}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
            />
          </div>

          {/* Active snooze */}
          {activeSnooze && (
            <div className="px-6 py-1 flex gap-2 items-center text-xs text-muted-foreground">
              <Bell className="w-3 h-3 shrink-0" />
              <p className="font-medium">{t("Snoozed until:")}</p>
              <p>
                {formatDateTime(new Date(String(activeSnooze.until)), "MMM D, YYYY at hh:mm a", {
                  unit: "weeks",
                  amount: 1,
                  direction: "both",
                })}
              </p>
            </div>
          )}

          {/* Calendar event inline (title/time on ticket without a full calendarEventId) */}
          {ticket.calendarEventTitle && (
            <div className="px-6 py-1 flex gap-2 items-center text-xs text-muted-foreground flex-wrap">
              <Calendar className="w-3 h-3 shrink-0" />
              <p className="font-medium">{t("Event:")}</p>
              <p>{ticket.calendarEventTitle}</p>
              {ticket.calendarEventStartTimeInMS && (
                <>
                  <span>·</span>
                  <p>
                    {formatDateTime(new Date(ticket.calendarEventStartTimeInMS), "MMM D, YYYY at hh:mm a", {
                      unit: "weeks",
                      amount: 1,
                      direction: "both",
                    })}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Parent / root ticket */}
          {ticket.parentTicketId && (
            <div className="py-1 flex gap-2 items-center text-xs text-muted-foreground flex-wrap">
              <GitBranch className="w-3 h-3 shrink-0" />
              <p className="font-medium">{t("Parent ticket:")}</p>
              <Link to={`/tickets/${ticket.parentTicketId}`} className="text-primary hover:underline">
                {parentTicketQuery.data?.title ?? ticket.parentTicketId}
              </Link>
              {ticket.rootTicketId && ticket.rootTicketId !== ticket.parentTicketId && (
                <>
                  <span>·</span>
                  <p className="font-medium">{t("Root:")}</p>
                  <Link to={`/tickets/${ticket.rootTicketId}`} className="text-primary hover:underline">
                    {ticket.rootTicketId}
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Journey */}
          {ticket.journeyId && (
            <div className="py-1 flex gap-2 text-xs text-muted-foreground items-center">
              <Workflow className="w-3 h-3 shrink-0" />
              <p className="font-medium">{t("Created by journey:")}</p>
              <Badge variant="outline" className="text-xs">
                {journeyQuery.data?.title ?? ticket.journeyId}
              </Badge>
            </div>
          )}

          {/* Related records */}
          {!!ticket.relatedRecords?.length && (
            <div className="py-1 flex gap-2 text-xs text-muted-foreground items-center">
              <p className="font-medium">{t("Related records:")}</p>
              <div className="flex flex-wrap gap-1">
                {ticket.relatedRecords.map((record, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {String(record.type ?? "")}
                    {record.type ? ": " : ""}
                    {String(record.id ?? record)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Canvas reference */}
          {hasCanvasReference && (
            <div className="py-1 flex gap-2 text-xs text-muted-foreground items-center">
              <p className="font-medium">{t("References:")}</p>
              {canvasURL ? (
                <a href={canvasURL} target="_blank" rel="noopener noreferrer">
                  <Badge variant="outline" className="text-xs hover:bg-muted cursor-pointer">
                    {t("Canvas reference")}
                  </Badge>
                </a>
              ) : (
                <Badge variant="outline" className="text-xs">
                  {t("Canvas reference")}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        {!!ticket.attachments?.length && (
          <div className="flex flex-wrap gap-2">
            {ticket.attachments.map((attachment) => (
              <TicketAttachment
                key={attachment.secureName}
                attachment={attachment}
                deleteFile
                onDelete={() => {
                  updateTicketMutation.mutate({
                    id: ticketId,
                    attachments: ticket.attachments?.filter((a) => a.secureName !== attachment.secureName),
                    _options: {
                      replaceObjectFields: true,
                    },
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Relationships & related contacts */}
      {(relationshipsWithContacts.length > 0 || relatedJourneyEndusers.length > 0) && (
        <div className="px-6 py-4 flex flex-col gap-1 text-xs text-muted-foreground border-b border-b-gray-200">
          <p className="font-medium text-sm mb-1">{t("Relationships")}</p>
          {relationshipsWithContacts.map(({ contact, type }) => (
            <div key={contact.id} className="flex items-center gap-4 py-1">
              <PersonCell
                person={contact}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openSidebar("ContactDetails", { enduserId: contact.id });
                }}
              />
              <Badge variant="secondary" className="text-xs">
                {type}
              </Badge>
            </div>
          ))}
          {relatedJourneyEndusers.length > 0 && (
            <>
              {relationshipsWithContacts.length > 0 && (
                <p className="font-medium text-sm mt-2 mb-1">{t("Related contacts")}</p>
              )}
              {relatedJourneyEndusers.map((relatedEnduser) => (
                <div key={relatedEnduser.id} className="flex items-center py-1">
                  <PersonCell
                    person={relatedEnduser}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openSidebar("ContactDetails", { enduserId: relatedEnduser.id });
                    }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Profile fields panel (contextEnduserFields or queue.enduserFields) */}
      {profileFieldKeys.length > 0 && enduser && (
        <div className="px-6 py-4 flex flex-col gap-1 text-xs text-muted-foreground border-b border-gray-200">
          <p className="font-medium text-sm mb-1">{t("Profile Information")}</p>
          {profileFieldKeys.map((fieldKey) => {
            const label = getProfileFieldLabel(fieldKey);
            const value = getProfileFieldValue(fieldKey);
            if (!value) return null;
            return (
              <div key={fieldKey} className="flex gap-2 items-center">
                <p className="font-medium">{label}:</p>
                <p>{value}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="overflow-y-auto flex-grow pb-4">
        {/* Description */}
        <div className="flex flex-col items-start w-full px-4 py-2 gap-2">
          {isEditingDescription ? (
            <Textarea
              autoFocus
              value={newDescription || ticket.message}
              onChange={(e) => setNewDescription(e.target.value)}
              style={{
                resize: "none",
                fieldSizing: "content",
              }}
              data-testid="ticket-description-textarea"
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="font-normal w-full justify-start text-wrap text-left h-min"
              onClick={() => setIsEditingDescription(true)}
              data-testid="ticket-description-btn"
            >
              {ticket.message || t("Enter a description...")}
            </Button>
          )}
        </div>

        {!!ticket.htmlDescription && (
          <div
            className="px-6 py-2 text-sm"
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHTMLContent(ticket.htmlDescription) }}
          />
        )}

        {/* Context cards (phone call, SMS, email, forms, event, etc.) */}
        <div className="w-full flex flex-col gap-2 px-6 py-2">
          <TicketContext ticketId={ticketId} ticket={ticket} />
        </div>

        {/* Ticket actions checklist */}
        <div className="w-full flex flex-col gap-2 px-6 py-2">
          <TicketActions ticketId={ticketId} ticket={ticket} />
        </div>

        {/* Floating save bar for description editing */}
        {isEditingDescription && (
          <div className="absolute bottom-4 left-4 right-4 z-100">
            <Item className="bg-foreground/90 backdrop-blur-sm border shadow-lg">
              <ItemContent>
                <ItemTitle className="text-white">{t("Edit Ticket...")}</ItemTitle>
                <ItemDescription className="text-white/80">{t("Updating ticket description")}</ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-foreground text-white border-white/20 hover:bg-white/10 hover:text-white"
                  onClick={() => {
                    setIsEditingDescription(false);
                    setNewDescription("");
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  size="sm"
                  className="bg-white text-black hover:bg-gray-100"
                  onClick={() => {
                    updateTicketMutation.mutateAsync({ id: ticketId, message: newDescription || ticket.message });
                    setIsEditingDescription(false);
                    setNewDescription("");
                  }}
                >
                  {t("Save Description")}
                </Button>
              </ItemActions>
            </Item>
          </div>
        )}
      </div>
    </div>
  );
};
